import { MemberSetModel } from "@app/members";
import { SpecialtyOverrides } from "@app/projects/create/interfaces";
import EditProjectOverrideMemberSet from "@app/projects/editProjectOverrideMemberSet";
import { TemplateSpecialtyGroup } from "@app/templates";
import sortBy from "lodash/sortBy";
import * as React from "react";
import { SortDirections, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { numberSort } from "@app/shared/waffles/exports";
import { JSX } from "react";
import { Accordion } from "@app/shared/Accordion";
import { AccordionContainer } from "@app/shared/AccordionContainer";
import Project from "@app/projects/project";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESSelect } from "@app/shared/qes-standards/forms/Select";
import { QESTextInput } from "@app/shared/qes-standards/forms/TextInput";
import { UserContextProps, withUserContext } from "@app/user/UserContext";

interface EditMembershipBindingProps {
  allMemberPopulations: MemberSetModel[];
  alternateMemberships: MemberSetModel[];
  canEdit: boolean;
  projectModel: Project;
  defaultOpen?: boolean;
  excludedSpecialtyGroupCodes: string[];
  handlePrimaryMembershipBindingChange: (memberPop: number, code: string) => void;
  handleSecondaryMembershipBindingChange: (memberPop: number, code: string) => void;
  projectMemberPopulations: MemberSetModel[];
  setPrimarySpecialtyOverride: (memberPop: number, code: string) => void;
  setSecondarySpecialtyOverride: (memberPop: number, code: string) => void;
  shared: boolean;
  specialtyGroups: TemplateSpecialtyGroup[];
  specialtyOverrides: SpecialtyOverrides[];
  uniqueKey: string;
  editProjectOverridesMembersets: EditProjectOverrideMemberSet[];
}

interface EditMembershipBindingState {
  disableMassBindingButton: boolean;
  massBindings: TemplateSpecialtyGroup[];
  panelTitle: string;
  showBinding: boolean;
  sortedRecords: TemplateSpecialtyGroup[];
  uniqueKey: string;
}

interface TableData {
  code: string;
  key: string;
  name: string;
  specialtyGroupPrimaryMembership: JSX.Element;
  specialtyGroupSecondaryMembership: JSX.Element;
}

class MembershipBindingTable extends React.Component<EditMembershipBindingProps & UserContextProps, EditMembershipBindingState> {
  private readonly sortOrder: Array<keyof TemplateSpecialtyGroup> = ["displayOrder"];
  private readonly panelTitle: string = "Member Population Binding";

  constructor(props: EditMembershipBindingProps & UserContextProps) {
    super(props);
    this.state = {
      disableMassBindingButton: false,
      massBindings: [],
      panelTitle: this.panelTitle,
      showBinding: false,
      sortedRecords: props.specialtyGroups && sortBy(props.specialtyGroups, this.sortOrder) || props.specialtyGroups,
      uniqueKey: props.uniqueKey,
    };
  }

  public componentDidUpdate() {
    if (this.props.uniqueKey !== this.state.uniqueKey) {
      this.setState({
        uniqueKey: this.props.uniqueKey,
      });
    }
  }

  public render() {
    const selectedKeys: string[] = this.state.massBindings.map((sr: TemplateSpecialtyGroup) => sr.code);

    if (!this.state.sortedRecords) {
      return null;
    }

    const records: TableData[] = this.state.sortedRecords.map((rec) => {
      return {
        code: rec.code,
        name: rec.name,
        specialtyGroupPrimaryMembership: (() => {
          if (this.props.shared) {
            return (
              <QESTextInput
                type="text"
                className="form-control"
                disabled={true}
                value={!!this.props.specialtyOverrides.filter((x) => x.code === rec.code)[0] && this.props.specialtyOverrides.filter((x) => x.code === rec.code)[0].primaryMembership ?
                  this.getAlternateDisplay(this.props.specialtyOverrides.filter((x) => x.code === rec.code)[0].primaryMembership, this.props.editProjectOverridesMembersets.filter((x) => x.memberSetId === (this.props.specialtyOverrides.filter((x) => x.code === rec.code)[0].primaryMembership))[0].memberSetName) : "Default Primary Member Population"}
                label=""
                testId="member-table__edit-project-primary-alternate-member-set-text-box"
              />
            );
          }
          else {
            return (
              <QESSelect
                label="Primary Membership"
                hideLabel={true}
                testId="edit-membership-binding__primary-membership"
                className="form-control"
                disabled={!this.props.canEdit}
                onChange={(e) => this.handlerPrimary(e, rec.code)}
                value={!!this.props.specialtyOverrides.filter((x) => x.code === rec.code)[0] && this.props.specialtyOverrides.filter((x) => x.code === rec.code)[0].primaryMembership ? this.props.specialtyOverrides.filter((x) => x.code === rec.code)[0].primaryMembership : ""}
              >
                <option key="0" value="0">Default Primary Member Population</option>
                {(this.sortArrayAfterFirstItem(this.props.alternateMemberships) as MemberSetModel[]).filter((option) => !!option).map((option) => (
                  option.hasActivatedVersions ?
                    <option key={option.memberSetId} value={option.memberSetId} disabled={!this.props.canEdit}>{this.getAlternateDisplay(option.memberSetId, option.resource.name)}</option> :
                    <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
                ))}
              </QESSelect>
            );
          }
        })(),
        specialtyGroupSecondaryMembership: (() => {
          if (this.props.shared) {
            return (
              <QESTextInput
                type="text"
                className="form-control"
                disabled={true}
                value={!!this.props.specialtyOverrides.filter((x) => x.code === rec.code)[0] && this.props.specialtyOverrides.filter((x) => x.code === rec.code)[0].secondaryMembership ?
                  this.getAlternateDisplay(this.props.specialtyOverrides.filter((x) => x.code === rec.code)[0].secondaryMembership, this.props.allMemberPopulations.filter((x) => x.memberSetId === (this.props.specialtyOverrides.filter((x) => x.code === rec.code)[0].secondaryMembership))[0].resource.name) : "Default Secondary Member Population"}
                testId="member-table__edit-project-secondary-alternate-member-set-text-box"
                label=""
              />
            );
          }
          else {
            return (
              <QESSelect
                label="Secondary Membership"
                hideLabel={true}
                testId="edit-membership-binding__secondary-membership"
                className="form-control"
                disabled={this.disableSecondary()}
                onChange={(e) => this.handlerSecondary(e, rec.code)}
                value={!!this.props.specialtyOverrides.filter((x) => x.code === rec.code)[0] && this.props.specialtyOverrides.filter((x) => x.code === rec.code)[0].secondaryMembership ? this.props.specialtyOverrides.filter((x) => x.code === rec.code)[0].secondaryMembership : ""}
              >
                <option key="0" value="0">Default Secondary Member Population</option>
                {(this.sortArrayAfterFirstItem(this.props.alternateMemberships) as MemberSetModel[]).filter((option) => !!option).map((option) => (
                  option.hasActivatedVersions ?
                    <option key={option.memberSetId} value={option.memberSetId} disabled={!this.props.canEdit}>{this.getAlternateDisplay(option.memberSetId, option.resource.name)}</option> :
                    <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
                ))}
              </QESSelect>
            );
          }

        })(),
        [wafflesKeyColumnName]: rec.code,
      };
    });

    const dualColumnConfig = {
      code: {
        displayText: "Code",
        testId: "code",
      },
      name: {
        displayText: "Description",
        testId: "description",
      },
      specialtyGroupPrimaryMembership: {
        displayText: "Primary Member Population",
        testId: "member-population-population",
      },
      specialtyGroupSecondaryMembership: {
        displayText: "Secondary Member Population",
        testId: "secondary-member-population",
      },
    };

    return (
      <div className="form-group">
        <div>
          <AccordionContainer>
            <Accordion
              title={this.state.panelTitle}
              defaultOpen={!!this.props.defaultOpen}
              testId={`edit-membership-binding-table__${this.panelTitle}-accordion`}
            >
              <React.Fragment>
                <div className="col-12">
                  <header className="page-title-bar row justify-content-between align-items-center mt-4 mb-3">
                    <p className="col-auto">Check the specialty groups to bind multiple...</p>
                    <QESButton qesTestId={`bind_memberships`} onClick={this.renderMassBinding} disabled={this.disableMassButton()} className="col-auto">Bind Memberships</QESButton>
                  </header>
                  {this.state.showBinding && (
                    <div>
                      <div className="form-group row col-5" style={{ marginTop: "1rem" }}>
                        <QESSelect
                          label="Alternate Primary Membership"
                          hideLabel={true}
                          testId="edit-membership-binding__alternate-primary"
                          className="form-control"
                          onChange={this.handlePrimaryMemberPopulationChange}
                        >
                          <option key="0" value="0">Default Primary Member Population</option>
                          {(this.sortArrayAfterFirstItem(this.props.alternateMemberships) as MemberSetModel[]).map((option) => (
                            option.hasActivatedVersions ?
                              <option key={option.memberSetId} value={option.memberSetId}>{this.getAlternateDisplay(option.memberSetId, option.resource.name)}</option> :
                              <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
                          ))}
                        </QESSelect>
                      </div>
                      <div className="form-group row col-5">
                        <QESSelect
                          label="Alternate Secondary Membership"
                          hideLabel={true}
                          testId="edit-membership-binding__alternate-secondary"
                          className="form-control"
                          disabled={this.disableSecondary()}
                          onChange={this.handleSecondaryMemberPopulationChange}
                        >
                          <option key="0" value="0">Default Secondary Member Population</option>
                          {(this.sortArrayAfterFirstItem(this.props.alternateMemberships) as MemberSetModel[]).map((option) => (
                            option.hasActivatedVersions ?
                              <option key={option.memberSetId} value={option.memberSetId}>{this.getAlternateDisplay(option.memberSetId, option.resource.name)}</option> :
                              <option key={option.memberSetId} value={option.memberSetId} disabled={true}>{option.resource.name} - No Activated Versions</option>
                          ))}
                        </QESSelect>
                      </div>
                    </div>
                  )}
                  <Waffles<TableData>
                    columns={dualColumnConfig}
                    customSort={this.customSort}
                    data={records.filter((e) => !this.props.excludedSpecialtyGroupCodes.includes(e.code))}
                    disableProcessingOverlay={true}
                    handleCheckboxChange={this.handleWaffleSelectionChange}
                    selectedKeys={selectedKeys}
                    showCheckboxes={this.props.canEdit}
                    testId="membership-bindings-table"
                  />
                </div>
              </React.Fragment>
            </Accordion>
          </AccordionContainer>
        </div>
      </div>
    );
  }

  private renderMassBinding = () => {
    const { showBinding } = this.state;
    this.setState({
      showBinding: !showBinding,
    });
  }

  private handlerPrimary = (e: React.ChangeEvent<HTMLSelectElement>, code: string) => {
    let memberPop = +(e.target.value);
    if (memberPop === 0) {
      memberPop = null;
    }
    this.props.setPrimarySpecialtyOverride(memberPop, code);
    this.props.handlePrimaryMembershipBindingChange(memberPop, code);
  }

  private handlerSecondary = (e: React.ChangeEvent<HTMLSelectElement>, code: string) => {
    let memberPop = +(e.target.value);
    if (memberPop === 0) {
      memberPop = null;
    }
    this.props.setSecondarySpecialtyOverride(memberPop, code);
    this.props.handleSecondaryMembershipBindingChange(memberPop, code);
  }

  private handlePrimaryMemberPopulationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    let memberPop = +(e.target.value);
    if (memberPop === 0) {
      memberPop = null;
    }
    this.state.massBindings.forEach((x) => {
      this.props.handlePrimaryMembershipBindingChange(memberPop, x.code);
      this.props.setPrimarySpecialtyOverride(memberPop, x.code);
    });
  }

  private handleSecondaryMemberPopulationChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    let memberPop = +(e.target.value);
    if (memberPop === 0) {
      memberPop = null;
    }
    this.state.massBindings.forEach((x) => {
      this.props.handleSecondaryMembershipBindingChange(memberPop, x.code);
      this.props.setSecondarySpecialtyOverride(memberPop, x.code);
    });
  }

  private handleWaffleSelectionChange = (selectedCodes: string[]) => {
    const selectedProps = this.state.sortedRecords.filter((sr) => {
      return selectedCodes.includes(sr.code);
    });
    this.setState({
      massBindings: selectedProps,
      uniqueKey: `reset${(Math.random() * 100).toFixed(0)}`,
    });
  }

  private sortArrayAfterFirstItem = (arr: MemberSetModel[]): MemberSetModel[] => {
    if (arr === null || arr === undefined) {
      return [];
    }

    let newArr = arr;
    const firstItem = newArr.shift();
    newArr = newArr.sort((a, b) => a.resource.name > b.resource.name ? 1 : -1);
    newArr.unshift(firstItem);
    return newArr;
  }

  private disableSecondary = () => !this.props.projectModel.memberSetId2;

  private disableMassButton = () => {
    if (this.state.massBindings.length === 0) {
      return true;
    } else {
      return false;
    }
  }

  private customSort = (records: TableData[], column: keyof TableData, direction: SortDirections) => {
    const data = [...records];
    if (direction === SortDirections.None) {
      return data;
    }
    const multiplier = direction === SortDirections.Asc ? 1 : -1;
    switch (column) {
      case "code":
        return numberSort<TableData>(records, column, multiplier);
      case "name":
        return data.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
      case "specialtyGroupPrimaryMembership":
        return data.sort((a, b) => (a.specialtyGroupPrimaryMembership.props.value > b.specialtyGroupPrimaryMembership.props.value ? 1 : -1) * multiplier);
      case "specialtyGroupSecondaryMembership":
        return data.sort((a, b) => (a.specialtyGroupSecondaryMembership.props.value > b.specialtyGroupSecondaryMembership.props.value ? 1 : -1) * multiplier);
      default:
        return data;
    }
  }

  private getAlternateDisplay = (memberPop: number, memberPopName: string) => {
    const updates: EditProjectOverrideMemberSet[] = this.props.projectModel.editProjectOverrideMemberSets;
    let alias = "";
    updates.forEach((x) => {
      if (x.memberSetId === memberPop) {
        if (x.alias !== "") {
          alias = x.alias;
        } else {
          alias = memberPopName;
        }
      }
    });
    return alias;
  }

}

const WrappedMembershipBindingTable = withUserContext(MembershipBindingTable);
export { WrappedMembershipBindingTable as MembershipBindingTable };
